import React from 'react';
import { UploadCloud, Trash2, Info } from 'lucide-react';
import Modal from '../../../components/Modal';
import { Button } from '../../../components';
import { groupChangesByType } from './scheduleChangeMeta';

// Opened from the Schedule header "Publish" button. Shows a grouped summary of
// every unpublished schedule edit, then confirms to make them live (sync +
// notify carers). Mirrors the duty-roster PublishReviewModal.
const PublishReviewModal = ({ open, changes = [], publishing, onPublish, onRemove, onClose }) => {
  const groups = groupChangesByType(changes);

  return (
    <Modal
      isOpen={open}
      onClose={() => { if (!publishing) onClose(); }}
      title="Review & publish schedule"
      maxWidth="max-w-2xl"
      footer={(
        <div className="flex items-center justify-between gap-3">
          <Button variant="secondary" type="button" onClick={onClose} disabled={publishing}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={onPublish}
            loading={publishing}
            disabled={changes.length === 0 || publishing}
            leftIcon={<UploadCloud className="w-4 h-4" />}
          >
            {publishing ? 'Publishing…' : `Publish ${changes.length} change${changes.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      )}
    >
      <div className="space-y-4 pt-1">
        {/* Make-live warning (from the DOM prototype) */}
        <div className="flex items-start gap-3 rounded-xl border border-[#B9CDF8] bg-[#F5F8FF] px-4 py-3 text-[12px] leading-relaxed text-[#3E5F96]">
          <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-lg bg-[#DCE8FF] text-[#245FCF]"><Info className="h-3.5 w-3.5" /></span>
          <div>
            <strong className="font-extrabold">Make these changes live?</strong><br />
            These edits are saved only in your browser so far. Publishing updates the package visits
            and assignments visible to carers. You can still cancel and keep editing.
          </div>
        </div>

        {/* Per-type summary chips */}
        {changes.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {groups.map((g) => (
              <span key={g.type} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold ${g.meta.cls}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${g.meta.dot}`} />
                {g.meta.group} · {g.items.length}
              </span>
            ))}
          </div>
        )}

        {/* Grouped detail */}
        {changes.length === 0 ? (
          <div className="py-10 text-center text-[13px] text-[#94A3B8] dark:text-gray-500">No unpublished changes to publish.</div>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => (
              <div key={g.type} className="rounded-xl border border-[#E2E8F0] dark:border-gray-800 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-[#F8FAFC] dark:bg-[#31373f] border-b border-[#EDF1F6] dark:border-gray-800">
                  <span className="flex items-center gap-2 text-[12px] font-extrabold text-[#334155] dark:text-gray-200">
                    <span className={`w-2 h-2 rounded-full ${g.meta.dot}`} />
                    {g.meta.group}
                  </span>
                  <span className="text-[11px] font-bold text-[#94A3B8] dark:text-gray-500">{g.items.length}</span>
                </div>
                <ul className="divide-y divide-[#EDF1F6]">
                  {g.items.map((c) => (
                    <li key={c.id} className="flex items-center gap-3 px-4 py-2.5">
                      <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-md bg-gray-100 text-[11px] text-gray-500 dark:text-gray-400">{g.meta.icon}</span>
                      <span className="flex-1 min-w-0 text-[12px] text-[#475569] dark:text-gray-400">{c.label}</span>
                      <button
                        type="button"
                        onClick={() => onRemove(c.id)}
                        disabled={publishing}
                        title="Remove this change"
                        className="shrink-0 w-7 h-7 grid place-items-center rounded-md border border-[#E2E8F0] dark:border-gray-700 text-[#94A3B8] dark:text-gray-500 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 transition disabled:opacity-40"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PublishReviewModal;
